/**
 * HowChipsWork — /how-chips-work
 *
 * Public trust-asset page that documents Invisique's two-tier chip
 * system. Referenced from the "How chips work" link under the trust
 * chip row on every doctor's landing page. Purpose:
 *   1. Signal that chips are earned, not marketed
 *   2. Give patients a clear taxonomy so they can read the chips well
 *   3. Give doctors visibility into what they can (and cannot) game
 *
 * iter100.51 (2026-02-28) — first cut. Copy is intentionally direct
 * and skeptical-friendly; every Tier-1 chip carries the same
 * `explanation` shown as tooltip on the landing page so the two
 * surfaces stay in sync.
 */
import React from "react";
import { Link } from "react-router-dom";
import {
    ShieldCheck, CheckCircle, Certificate, Star, UsersFour, Trophy,
    SealCheck, Sparkle, Baby, Lightning, Wheelchair, GenderFemale,
    FirstAid, House, CreditCard, Rainbow, CalendarBlank, ScanSmiley,
    Buildings, Translate, ArrowRight, MagnifyingGlass, ArrowsClockwise,
} from "@phosphor-icons/react";

const TIER1 = [
    {
        section: "Auto-verified",
        source: "auto",
        chips: [
            {
                icon: Certificate,
                name: "Invisique™ Certified",
                earned: "Passes Invisique™'s clinical vetting — credentials, orthodontic training and case-planning ability reviewed by our clinical panel before they can join.",
                gameable: "No. Cannot be self-awarded, cannot be bought.",
            },
            {
                icon: ShieldCheck,
                name: "DCI Verified",
                earned: "Their DCI number is cross-checked against the Dental Council of India's public register. If the number doesn't resolve, the chip doesn't render.",
                gameable: "No. Sourced directly from the DCI register.",
            },
            {
                icon: CheckCircle,
                name: "Cases treated",
                earned: "A live count of aligner cases planned and delivered on the Invisique™ platform. Ticks up automatically as they treat more patients. Only cases that progressed past draft/cancelled count.",
                gameable: "No. Pulled directly from case records; doctors can hide the chip but can't inflate the number.",
            },
        ],
    },
    {
        section: "Admin-curated",
        source: "admin",
        chips: [
            {
                icon: Star,
                name: "Featured 2026",
                earned: "Hand-picked by the Invisique™ editorial team as a Featured Provider for the year — chosen for case quality, patient outcomes and clinic experience.",
                gameable: "Curated. Only the Invisique™ team can grant it, and it expires yearly.",
            },
            {
                icon: UsersFour,
                name: "Invisique™ Panel Member",
                earned: "Sits on our clinical advisory panel. Panel members help shape treatment protocols and review complex cases.",
                gameable: "No. Invitation only.",
            },
            {
                icon: Trophy,
                name: "5+ years with Invisique™",
                earned: "Has been an active Invisique-certified provider for at least five years — computed from their onboarding date.",
                gameable: "No. Time-based.",
            },
            {
                icon: SealCheck,
                name: "ISO 9001 Clinic",
                earned: "The clinic operates under an audited ISO 9001 quality-management system. Certificate is verified before the chip is granted.",
                gameable: "Verifiable. Requires proof of certification.",
            },
            {
                icon: Certificate,
                name: "Board Certified",
                earned: "Holds current board certification in orthodontics from a recognised speciality board.",
                gameable: "Verifiable. Board registration cross-checked.",
            },
            {
                icon: Trophy,
                name: "Top Rated",
                earned: "Ranks in the top decile of Invisique™ providers by verified patient reviews over the last 12 months.",
                gameable: "No. Computed from reviews we've verified against real appointments — not open reviews from the internet.",
            },
        ],
    },
];

const TIER2 = [
    { icon: Baby,          name: "Kids-friendly",         hint: "Comfortable treating paediatric orthodontic cases." },
    { icon: Lightning,     name: "Same-day slots",        hint: "Often has appointment slots the same day you enquire." },
    { icon: Wheelchair,    name: "Wheelchair accessible", hint: "Clinic is wheelchair-accessible." },
    { icon: GenderFemale,  name: "Female practitioner",   hint: "Lead practitioner is a woman." },
    { icon: FirstAid,      name: "Emergency care",        hint: "Offers out-of-hours emergency care." },
    { icon: House,         name: "Home visits",           hint: "Provides in-home visits under agreed conditions." },
    { icon: CreditCard,    name: "EMI available",         hint: "Treatment payable via EMI / no-cost EMI." },
    { icon: Rainbow,       name: "LGBTQ+ friendly",       hint: "Clinic is committed to inclusive care." },
    { icon: CalendarBlank, name: "Weekend hours",         hint: "Consults on Saturday and/or Sunday.", derived: true },
    { icon: ScanSmiley,    name: "3D scanner on-site",    hint: "Has an intraoral scanner in the clinic (no goopy impressions).", derived: true },
    { icon: Buildings,     name: "Multiple locations",    hint: "Practises across more than one clinic.", derived: true },
    { icon: Translate,     name: "Multilingual",          hint: "Consults in more than three languages.", derived: true },
];

export default function HowChipsWork() {
    return (
        <div className="min-h-screen bg-slate-50" data-testid="how-chips-work-page">
            <title>How Invisique™ chips work · Invisique™</title>
            <meta
                name="description"
                content="Chips on Invisique™ doctor profiles are earned, not marketed. Here's exactly how each one is awarded, and what patients should trust."
            />

            {/* Hero */}
            <section className="bg-white border-b border-slate-200">
                <div className="max-w-4xl mx-auto px-5 sm:px-8 py-10 lg:py-14">
                    <div className="overline text-primary mb-2">Trust language</div>
                    <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">
                        How Invisique™ chips work.
                    </h1>
                    <p className="mt-4 text-[15px] leading-relaxed text-slate-600 max-w-2xl">
                        Every chip you see on an Invisique™ doctor&apos;s profile falls into one of two tiers.
                        Tier 1 chips are our promises to you — earned through platform behaviour, verified
                        by our team, or computed from real case data. Tier 2 chips describe practice
                        attributes that help you filter, but aren&apos;t proof of quality on their own.
                    </p>
                    <p className="mt-3 text-[15px] leading-relaxed text-slate-600 max-w-2xl">
                        We wrote this page so you never have to wonder what a chip means, or whether
                        a doctor gave it to themselves.
                    </p>
                </div>
            </section>

            {/* Tier 1 */}
            <section className="max-w-4xl mx-auto px-5 sm:px-8 py-10 lg:py-14" data-testid="chips-tier-1">
                <div className="flex items-baseline justify-between gap-4 mb-1 flex-wrap">
                    <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
                        Tier 1 · Invisique™ Signals
                    </h2>
                    <span className="inline-flex items-center gap-1 h-6 px-2 rounded-full text-[11px] font-bold uppercase tracking-wider bg-primary/10 text-primary border border-primary/20">
                        Earned
                    </span>
                </div>
                <p className="text-[14px] text-slate-600 mb-8">
                    Signals we stand behind. If we get one wrong, tell us — we&apos;ll revoke the chip.
                </p>

                <div className="space-y-10">
                    {TIER1.map((group) => (
                        <div key={group.section}>
                            <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500 mb-4">
                                {group.section}
                            </div>
                            <div className="grid grid-cols-1 gap-3">
                                {group.chips.map((c) => (
                                    <ChipCard key={c.name} chip={c} source={group.source} />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Tier 2 */}
            <section className="bg-white border-t border-slate-200">
                <div className="max-w-4xl mx-auto px-5 sm:px-8 py-10 lg:py-14" data-testid="chips-tier-2">
                    <div className="flex items-baseline justify-between gap-4 mb-1 flex-wrap">
                        <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
                            Tier 2 · Practice attributes
                        </h2>
                        <span className="inline-flex items-center gap-1 h-6 px-2 rounded-full text-[11px] font-bold uppercase tracking-wider bg-slate-100 text-slate-600 border border-slate-200">
                            Self-attested
                        </span>
                    </div>
                    <p className="text-[14px] text-slate-600 mb-8 max-w-2xl">
                        Doctors self-declare these to help you filter — &ldquo;I need a female practitioner&rdquo;, &ldquo;I need
                        weekend slots&rdquo;, &ldquo;does the clinic offer EMI?&rdquo;. They&apos;re useful, but treat them like
                        preferences, not proof.
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {TIER2.map((c) => {
                            const p = CHIP_PALETTES.doctor;
                            return (
                                <div
                                    key={c.name}
                                    className="flex items-start gap-3 rounded-xl border px-4 py-3"
                                    style={{ borderColor: p.border, backgroundColor: p.light }}
                                    data-testid={`t2-chip-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                                >
                                    <div
                                        className="h-8 w-8 rounded-lg bg-white border flex items-center justify-center shrink-0"
                                        style={{ borderColor: p.border }}
                                    >
                                        <c.icon size={14} weight="duotone" style={{ color: p.accent }} />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="text-[13px] font-semibold text-slate-800 flex items-center gap-2">
                                            {c.name}
                                            {c.derived && (
                                                <span className="text-[9px] font-bold uppercase tracking-wider bg-white text-slate-600 border border-slate-200 px-1.5 rounded">
                                                    Auto-detected
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-[12.5px] text-slate-500 leading-relaxed">
                                            {c.hint}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* iter100.52 — Chip lifecycle: computed live, so metric drops
                automatically revoke chips. Explicit callout builds trust
                that a chip today can't linger falsely tomorrow. */}
            <section className="bg-white border-t border-slate-200">
                <div className="max-w-4xl mx-auto px-5 sm:px-8 py-10 lg:py-14" data-testid="chips-lifecycle">
                    <div className="flex items-start gap-4">
                        <div className="h-10 w-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                            <ArrowsClockwise size={18} weight="fill" className="text-primary" />
                        </div>
                        <div>
                            <h2 className="font-display text-xl sm:text-2xl font-bold text-slate-900 tracking-tight mb-2">
                                How chips retire.
                            </h2>
                            <p className="text-[14px] text-slate-700 leading-relaxed max-w-2xl">
                                Every Tier-1 chip is re-computed the moment you load a doctor&apos;s profile. If a signal drops
                                — for example, a doctor&apos;s DCI registration lapses, or their case data suggests they no
                                longer meet a volume band — the chip disappears on the very next visit. There is no
                                &ldquo;earned-forever&rdquo; state; chips are a live snapshot, not a trophy.
                            </p>
                            <p className="text-[13px] text-slate-500 leading-relaxed max-w-2xl mt-2">
                                Admin-curated chips (Featured 2026, Panel Member, ISO 9001) can also be revoked manually
                                by the Invisique™ team when the underlying condition changes.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* What's coming */}
            <section className="max-w-4xl mx-auto px-5 sm:px-8 py-10 lg:py-14" data-testid="chips-roadmap">
                <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
                    <div className="overline text-primary mb-2">Next up</div>
                    <h2 className="font-display text-xl sm:text-2xl font-bold text-slate-900 tracking-tight mb-3">
                        Chips we&apos;re building next.
                    </h2>
                    <p className="text-[14px] text-slate-600 mb-5 max-w-2xl">
                        These need a bit more data to compute honestly. When we ship them, we&apos;ll list them here
                        alongside the exact formula.
                    </p>
                    <ul className="space-y-3 text-[13.5px] text-slate-700">
                        <li className="flex items-start gap-2">
                            <Sparkle size={14} weight="fill" className="text-primary shrink-0 mt-0.5" />
                            <span>
                                <span className="font-semibold text-slate-900">On-Time Aligner</span> — awarded when
                                the doctor delivers ≥ 90% of aligner batches within promised timelines. Waiting on aligner-shipment SLA tracking (promised vs actual delivery dates on each batch) before we can compute this honestly.
                            </span>
                        </li>
                        <li className="flex items-start gap-2">
                            <Sparkle size={14} weight="fill" className="text-primary shrink-0 mt-0.5" />
                            <span>
                                <span className="font-semibold text-slate-900">Refinement-Light</span> — when a
                                doctor&apos;s refinement rate sits below the Invisique™ median. Ships once we&apos;ve got enough refinement history across the network to establish a fair median.
                            </span>
                        </li>
                        <li className="flex items-start gap-2">
                            <Sparkle size={14} weight="fill" className="text-primary shrink-0 mt-0.5" />
                            <span>
                                <span className="font-semibold text-slate-900">Patient-Rated</span> — average verified
                                patient rating ≥ 4.5 with at least 10 reviews. Ships alongside our Verified Reviews launch.
                            </span>
                        </li>
                    </ul>
                </div>
            </section>

            {/* Bottom CTA */}
            <section className="bg-primary/5 border-t border-primary/15">
                <div className="max-w-4xl mx-auto px-5 sm:px-8 py-10 lg:py-12 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-8">
                    <div className="flex-1">
                        <h2 className="font-display text-lg sm:text-xl font-bold text-slate-900 mb-1">
                            Ready to find a certified provider?
                        </h2>
                        <p className="text-[13.5px] text-slate-600">
                            Filter by chip on the Find-a-Doctor page. Every doctor listed is Invisique™ Certified.
                        </p>
                    </div>
                    <Link
                        to="/find-doctor"
                        className="inline-flex items-center gap-1.5 h-11 px-5 rounded-full bg-primary text-primary-foreground text-sm font-semibold hover:brightness-95 transition-all shadow-sm"
                        data-testid="how-chips-work-cta"
                    >
                        <MagnifyingGlass size={14} weight="bold" />
                        Find a doctor
                    </Link>
                </div>
            </section>
        </div>
    );
}


/* iter100.53 — Mirror of the palette map in DoctorLandingPage.jsx so
   the trust-asset page speaks the same visual language as the chips
   themselves. Keeping them in sync is a manual promise for now; if
   this ever forks, extract to a shared module. */
const CHIP_PALETTES = {
    auto:    { light: "hsl(192 90% 96%)", border: "hsl(192 90% 82%)", text: "hsl(192 90% 25%)", accent: "hsl(192 90% 33%)" },
    admin:   { light: "hsl(340 78% 97%)", border: "hsl(340 78% 88%)", text: "hsl(340 65% 38%)", accent: "hsl(340 75% 45%)" },
    doctor:  { light: "hsl(150 55% 96%)", border: "hsl(150 45% 82%)", text: "hsl(150 60% 28%)", accent: "hsl(150 60% 35%)" },
    derived: { light: "hsl(150 55% 96%)", border: "hsl(150 45% 82%)", text: "hsl(150 60% 28%)", accent: "hsl(150 60% 35%)" },
};

function ChipCard({ chip, source = "auto" }) {
    const Icon = chip.icon;
    const p = CHIP_PALETTES[source] || CHIP_PALETTES.auto;
    return (
        <div
            className="rounded-xl border bg-white overflow-hidden transition-colors"
            style={{ borderColor: p.border }}
            data-testid={`t1-chip-${chip.name.toLowerCase().replace(/\s+/g, "-")}`}
        >
            <div className="p-5 flex items-start gap-4">
                <div
                    className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0 border"
                    style={{ backgroundColor: p.light, borderColor: p.border }}
                >
                    <Icon size={18} weight="fill" style={{ color: p.accent }} />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-display text-[15px] font-bold text-slate-900">{chip.name}</div>
                    </div>
                    <p className="text-[13px] text-slate-700 leading-relaxed mt-1">
                        <span className="font-semibold text-slate-900">Earned by · </span>
                        {chip.earned}
                    </p>
                    <p className="text-[12.5px] text-slate-500 leading-relaxed mt-1.5">
                        <span className="font-semibold text-slate-700">Can it be gamed? · </span>
                        {chip.gameable}
                    </p>
                </div>
            </div>
        </div>
    );
}
